import { splitReadMore } from "@/lib/read-more";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Rend une remarque / note publique (HTML) en gérant le marqueur « Lire plus » :
 * la partie avant le marqueur est toujours visible, la suite est révélée par un
 * bouton « Lire plus » / « Lire moins ». Sans marqueur, rend le HTML tel quel.
 *
 * Drop-in pour un `<div className dangerouslySetInnerHTML />` : le `className`
 * (styles du contenu) est appliqué aux blocs de contenu.
 *
 * Libellés : clés de traduction `catalogueReadMore` / `catalogueReadLess`
 * (éditables), avec repli localisé par langue si la clé n'est pas définie.
 */
const LABELS: Record<string, { more: string; less: string }> = {
  fr: { more: "Lire plus", less: "Lire moins" },
  en: { more: "Read more", less: "Read less" },
  es: { more: "Leer más", less: "Leer menos" },
  nl: { more: "Lees meer", less: "Lees minder" },
};

interface ReadMoreHtmlProps {
  html: string | null | undefined;
  className?: string;
  locale?: string;
}

export function ReadMoreHtml({ html, className, locale }: ReadMoreHtmlProps) {
  const { t } = useTranslation();
  const { before, after } = useMemo(() => splitReadMore(html), [html]);
  const [open, setOpen] = useState(false);
  const fallback = LABELS[locale ?? "fr"] ?? LABELS.fr;
  const labels = {
    more: t("catalogueReadMore", { defaultValue: fallback.more }),
    less: t("catalogueReadLess", { defaultValue: fallback.less }),
  };

  if (!after) {
    return (
      <div className={className} dangerouslySetInnerHTML={{ __html: before }} />
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <div className={className} dangerouslySetInnerHTML={{ __html: before }} />
      {open && (
        <div className={className} dangerouslySetInnerHTML={{ __html: after }} />
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-muted-foreground text-sm font-semibold no-underline hover:opacity-80"
      >
        {open ? labels.less : labels.more}
      </button>
    </div>
  );
}

export default ReadMoreHtml;
